import { type ReactNode, useState } from 'react';
import {
  AppShell as MantineAppShell,
  Group,
  ActionIcon,
  Box,
  useMantineColorScheme,
} from '@mantine/core';
import { IconSun, IconMoon } from '@tabler/icons-react';
import { useMediaQuery } from '@mantine/hooks';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '@/hooks/useAuth';
import { DesktopSidebar, type DesktopNavKey } from '@/components/ri/DesktopSidebar';
import { ScalePadMark } from '@/components/ri/ScalePadMark';
import { useCollapsiblePref, NAV_COLLAPSED_KEY } from '@/components/layout/nav/useCollapsiblePref';
import { MobileTabBar, type MobileTab } from '@/components/ri/MobileTabBar';
import { NotificationBell } from '@/components/layout/NotificationBell';
import { UserMenuPopover } from '@/components/layout/nav/UserMenuPopover';
import { MobileMoreSheet } from '@/components/layout/nav/MobileMoreSheet';
import { MOBILE_NAV_QUERY, desktopNavHref, isEventModePath } from '@/components/layout/nav/primaryRoutes';
import { GlobalSearch, GlobalSearchTrigger } from '@/components/layout/GlobalSearch';
import { ImpersonationBanner, IMPERSONATION_BANNER_HEIGHT } from '@/components/layout/ImpersonationBanner';
import { getImpersonation } from '@/lib/impersonation';
import { canAccessAdministration, canApprove } from '@/auth/roleAccess';
import { useEventIntelligenceAccess } from '@/features/events/useEventIntelligenceAccess';

const MOBILE_TAB_HREF: Record<Exclude<MobileTab, 'more'>, string> = {
  home: '/',
  accounts: '/accounts',
  quotes: '/quotes',
  approvals: '/approvals',
};

const COMPACT_NAV_QUERY = '(max-width: 70em)';

function desktopKeyFromPath(pathname: string): DesktopNavKey {
  if (pathname.startsWith('/how-to')) return 'how-to';
  if (pathname.startsWith('/whats-new')) return 'whats-new';
  if (pathname.startsWith('/events')) return 'events';
  if (pathname.startsWith('/admin')) return 'admin';
  if (pathname.startsWith('/accounts')) return 'accounts';
  if (pathname.startsWith('/quotes')) return 'quotes';
  if (pathname.startsWith('/approvals')) return 'approvals';
  return 'home';
}

function mobileTabFromPath(pathname: string): MobileTab {
  if (pathname.startsWith('/accounts')) return 'accounts';
  if (pathname.startsWith('/quotes')) return 'quotes';
  if (pathname.startsWith('/approvals')) return 'approvals';
  if (pathname === '/' || pathname === '') return 'home';
  return 'more';
}

export interface AppShellLayoutProps {
  children: ReactNode;
  navBadges?: Partial<Record<DesktopNavKey, number>>;
  mobileBadges?: Partial<Record<MobileTab, number>>;
  onMoreTab?: () => void;
  userMenu?: ReactNode;
}

export function AppShellLayout({
  children,
  navBadges,
  mobileBadges,
  onMoreTab,
  userMenu,
}: AppShellLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [moreOpen, setMoreOpen] = useState(false);
  const [userCollapsed, toggleUserCollapsed] = useCollapsiblePref(NAV_COLLAPSED_KEY, true);
  const compactViewport = useMediaQuery(COMPACT_NAV_QUERY) ?? false;
  const mobileViewport = useMediaQuery(MOBILE_NAV_QUERY, undefined, { getInitialValueInEffect: false }) ?? false;
  const eventMode = isEventModePath(location.pathname);
  const headerCollapsed = eventMode && mobileViewport;

  const navCollapsed = compactViewport || userCollapsed;
  const navWidth = navCollapsed ? 64 : 220;

  const desktopActive = desktopKeyFromPath(location.pathname);
  const mobileActive = mobileTabFromPath(location.pathname);

  const showApprovals = canApprove(user?.role);
  const showAdmin = canAccessAdministration(user?.role);
  const { enabled: showEvents } = useEventIntelligenceAccess();

  const impersonating = getImpersonation() !== null || user?.impersonation != null;
  const bannerOffset = impersonating
    ? `calc(${IMPERSONATION_BANNER_HEIGHT}px + env(safe-area-inset-top, 0px))`
    : '0px';

  const handleDesktopNavigate = (key: DesktopNavKey) => {
    navigate(desktopNavHref(key));
  };

  const handleMobileTab = (tab: MobileTab) => {
    if (tab === 'more') {
      if (onMoreTab) onMoreTab();
      else setMoreOpen(true);
      return;
    }
    navigate(MOBILE_TAB_HREF[tab]);
  };

  return (
    <>
      <ImpersonationBanner />
      <MantineAppShell
        layout="alt"
        header={{ height: 56, collapsed: headerCollapsed }}
        navbar={{
          width: navWidth,
          breakpoint: 'sm',
          collapsed: { mobile: true },
        }}
        padding={0}
        styles={{
          root: {
            '--app-shell-header-offset': headerCollapsed
              ? `calc(env(safe-area-inset-top, 0px) + ${bannerOffset})`
              : `calc(56px + env(safe-area-inset-top, 0px) + ${bannerOffset})`,
          } as React.CSSProperties,
          header: {
            backgroundColor: 'var(--surface-base)',
            borderBottom: '1px solid var(--border-subtle)',
            height: 'calc(56px + env(safe-area-inset-top, 0px))',
            paddingTop: 'env(safe-area-inset-top, 0px)',
            top: bannerOffset,
          },
          navbar: {
            backgroundColor: 'var(--surface-sunken)',
            borderRight: 'none',
            paddingTop: 'env(safe-area-inset-top, 0px)',
            top: bannerOffset,
            transition: 'width 0.18s ease',
          },
          main: {
            backgroundColor: 'var(--surface-base)',
            transition: 'padding-inline-start 0.18s ease',
          },
        }}
      >
        <MantineAppShell.Header>
          <HeaderContent userMenu={userMenu} showApprovals={showApprovals} />
        </MantineAppShell.Header>

        <MantineAppShell.Navbar>
          <DesktopSidebar
            active={desktopActive}
            collapsed={navCollapsed}
            badges={navBadges}
            showAdmin={showAdmin}
            showAccounts={user != null}
            showApprovals={showApprovals}
            showEvents={showEvents}
            onNavigate={handleDesktopNavigate}
            onToggleCollapse={() => toggleUserCollapsed()}
            showToggle={!compactViewport}
          />
        </MantineAppShell.Navbar>

        <MantineAppShell.Main>
          <Box
            p={eventMode ? { base: 0, sm: 16, md: 32 } : { base: 16, md: 32 }}
            pb={{ base: 'var(--mobile-tab-bar-clearance)', sm: 32 }}
            mih="calc(100vh - 56px)"
            style={{
              '--mobile-tab-bar-clearance': 'calc(100px + env(safe-area-inset-bottom, 0px))',
            } as React.CSSProperties}
          >
            {children}
          </Box>
        </MantineAppShell.Main>
      </MantineAppShell>

      {!eventMode && <Box
        component="nav"
        aria-label="Mobile navigation"
        hiddenFrom="sm"
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          height: 'calc(68px + env(safe-area-inset-bottom, 0px))',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          background: 'var(--surface-raised)',
          zIndex: 200,
        }}
      >
        <Box style={{ position: 'relative', height: 68 }}>
          <MobileTabBar
            active={mobileActive}
            badges={mobileBadges}
            showAccounts={user != null}
            showApprovals={showApprovals}
            onChange={handleMobileTab}
          />
        </Box>
      </Box>}

      <MobileMoreSheet open={moreOpen} onClose={() => setMoreOpen(false)} showEvents={showEvents} />
      <GlobalSearch />
    </>
  );
}

function HeaderContent({ userMenu, showApprovals }: { userMenu?: ReactNode; showApprovals?: boolean }) {
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();

  return (
    <Group h="100%" px="md" justify="space-between">
      <Group gap="sm">
        <Box hiddenFrom="sm" style={{ display: 'flex', alignItems: 'center' }}>
          <ScalePadMark size={27} title="ScalePad Meridian" />
        </Box>
      </Group>

      <Group gap="xs">
        <GlobalSearchTrigger />
        <ActionIcon
          variant="subtle"
          size="lg"
          color="gray"
          onClick={toggleColorScheme}
          title={colorScheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {colorScheme === 'dark' ? <IconSun size={18} /> : <IconMoon size={18} />}
        </ActionIcon>
        <NotificationBell showApprovals={showApprovals} />
        {userMenu ?? <UserMenuPopover />}
      </Group>
    </Group>
  );
}
